import { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { store } from '../../../lib/store'
import { circleDeleteRequest } from '../../../action/circle-actions'
import { openShapePopup } from '../buildShapePopupContent'
import { normalizeTrackWidth } from '../../../lib/trackColor'

const SRC = 'bm-circles-src'
const FILL = 'bm-circles-fill'
const LINE = 'bm-circles-line'

function circleToPolygon(centerLat, centerLng, radiusMeters, numPoints = 64) {
  const R = 6371000
  const lat = centerLat * Math.PI / 180
  const lng = centerLng * Math.PI / 180
  const d = radiusMeters / R
  const coords = []
  for (let i = 0; i <= numPoints; i++) {
    const angle = (i * 2 * Math.PI) / numPoints
    const lat2 = Math.asin(Math.sin(lat) * Math.cos(d) + Math.cos(lat) * Math.sin(d) * Math.cos(angle))
    const lng2 = lng + Math.atan2(Math.sin(angle) * Math.sin(d) * Math.cos(lat), Math.cos(d) - Math.sin(lat) * Math.sin(lat2))
    coords.push([lng2 * 180 / Math.PI, lat2 * 180 / Math.PI])
  }
  return coords
}

function buildFC(circles) {
  const features = []
  for (const circle of circles) {
    let geoJson; try { geoJson = circle.geoJson && JSON.parse(circle.geoJson) } catch { continue }
    if (!geoJson) continue
    const feat = geoJson.features?.[0]
    if (!feat) continue
    const [lng, lat] = feat.geometry.coordinates
    const radius = feat.properties?.radius
    if (!radius) continue
    const ringCoords = circleToPolygon(lat, lng, radius)
    const props = feat.properties?.line ?? {}
    features.push({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [ringCoords] },
      properties: {
        ...feat.properties,
        _id: circle.id,
        _strokeColor: props.color ?? '#EA4335',
        _fillColor: props.fill ?? props.color ?? '#EA4335',
        _width: normalizeTrackWidth(props.width),
      },
    })
  }
  return { type: 'FeatureCollection', features }
}

function teardown(m) {
  if (!m) return
  if (m.getLayer(FILL)) m.removeLayer(FILL)
  if (m.getLayer(LINE)) m.removeLayer(LINE)
  if (m.getSource(SRC)) m.removeSource(SRC)
}

const RenderCircles3D = ({ map, circles }) => {
  const activeMapRef = useRef(null)

  useEffect(() => {
    if (!map) return

    if (activeMapRef.current && activeMapRef.current !== map) {
      teardown(activeMapRef.current)
    }
    activeMapRef.current = map

    // Extracted so it can run both now and again on style.load. Shapes used to be
    // redrawn only by the styleKey remount, which waits for idle — i.e. for every
    // tile to settle — so a slow layer server delayed them by seconds.
    const apply = () => {
      const fc = buildFC(circles)

      if (map.getSource(SRC)) {
        map.getSource(SRC).setData(fc)
        return
      }

      map.addSource(SRC, { type: 'geojson', data: fc })
      map.addLayer({
        id: FILL,
        type: 'fill',
        source: SRC,
        paint: { 'fill-color': ['get', '_fillColor'], 'fill-opacity': 0.3 },
      })
      map.addLayer({
        id: LINE,
        type: 'line',
        source: SRC,
        paint: { 'line-color': ['get', '_strokeColor'], 'line-width': ['get', '_width'], 'line-opacity': 0.85 },
      })

      let lastClickTime = 0
      const clickHandler = (e) => {
        const now = Date.now()
        if (now - lastClickTime < 100) return
        lastClickTime = now
        const feat = e.features?.[0]
        if (!feat) return
        const id = feat.properties._id
        const circle = store.getState().circles.find(c => c.id === id)
        if (!circle) return
        let popup
        const onDelete = () => {
          popup?.remove()
          store.dispatch(circleDeleteRequest(id))
        }
        popup = openShapePopup(map, circle, 'circle', e.lngLat, onDelete)
      }
      const enterHandler = () => { map.getCanvas().style.cursor = 'pointer' }
      const leaveHandler = () => { map.getCanvas().style.cursor = '' }

      map.on('click', FILL, clickHandler)
      map.on('click', LINE, clickHandler)
      map.on('mouseenter', FILL, enterHandler)
      map.on('mouseenter', LINE, enterHandler)
      map.on('mouseleave', FILL, leaveHandler)
      map.on('mouseleave', LINE, leaveHandler)
    }

    apply()
    // Re-add on style.load, which fires as soon as the new style is parsed —
    // addSource only needs that, not the fully-loaded style isStyleLoaded() waits
    // for. The styleKey remount still happens later at idle; apply is idempotent
    // (setData when the source already exists) so running twice is harmless.
    map.on('style.load', apply)
    return () => map.off('style.load', apply)
  }, [map, circles])

  useEffect(() => {
    return () => teardown(activeMapRef.current)
  }, [])

  return null
}

const mapStateToProps = state => ({ circles: state.circles })
export default connect(mapStateToProps)(RenderCircles3D)
